import React, { useEffect } from "react";
// Rate Schedule Form

// Default
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Custom
import Layout from "../../layout";
import ContainedButton from "../../forms/button/ContainedButton";
import OutlinedButton from "../../forms/button/OutlinedButton";
import CustomOutlinedSelect from "../../forms/select/OutlinedCustomSelect";
import CustomInput from "../../forms/input/Input";
import CustomSwitch from "../switch/Switch";
import DatePickerInput from "../../forms/input/DatePickerInput";
import CustomDataTable from "../table/CustomTable";

// Mui
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid2";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
} from "@mui/material";
import {
  RateType,
  TariffCreateT,
  TariffResponseT,
  ToastState,
} from "../../types";
import { formatDate } from "../../utils/helper";
import CustomToast from "../toast/CustomToast";
import axios, { AxiosError } from "axios";

// Types
interface SelectOption {
  value: string;
  label: string;
}

interface TOURate {
  name: string;
  startTime: string;
  endTime: string;
  rate: string;
  showOnBill: boolean;
  description: string;
}

interface Block {
  minConsumption: string;
  maxConsumption: string;
  rate: string;
  tierName: string;
  showOnBill: boolean;
  rateUnit: string;
}

interface FlatRate {
  rateUnit: string;
  showOnBill: boolean;
  rate: string;
}

interface Tax {
  rateUnit: string;
  showOnBill: boolean;
  rate: string;
}

const COLUMNS = [
  "Period Name",
  "Description",
  "StartTime",
  "EndTime",
  "Rates",
  "Show on Bill",
  "Action",
];
const FlatRateColumns = [
  "Description",
  "Rate (₹/kWh)",
  "Show on Bill",
  "Action",
];
const TaxColumns = ["Description", "Percentage (%)", "Show on Bill", "Action"];

const COLUMNS1 = [
  "Description",
  "From (kWh)",
  "To (kWh)",
  "Rate (₹/kWh)",
  "Show on Bill",
  "Action",
];

type TariffDataT = {
  scheduleName: string;
  commodityType: string;
  customerCategory: string;
  effectiveDate: Date | null;
  timeOfUseRates: boolean;
};

const RateScheduleForm: React.FC = () => {
  const location = useLocation();
  const [toast, setToast] = useState<ToastState>({
    message: "",
    severity: "info",
  });

  const [status, setStatus] = useState<"draft" | "active" | "inactive">(
    "active",
  );
  const [tariffData, setTariffData] = useState<TariffDataT>({
    scheduleName: "",
    commodityType: "",
    customerCategory: "",
    effectiveDate: new Date(),
    timeOfUseRates: false,
  });

  const [flatRates, setFlatRates] = useState<FlatRate[]>([
    { rateUnit: "", rate: "", showOnBill: false },
  ]);
  const [taxes, setTaxes] = useState<Tax[]>([
    { rateUnit: "", rate: "", showOnBill: false },
  ]);
  const [hasSeasonalRates, setHasSeasonalRates] = useState(false);
  const [blocks, setBlocks] = useState<Block[]>([
    {
      minConsumption: "",
      maxConsumption: "",
      rate: "",
      tierName: "",
      rateUnit: "per kWh",
      showOnBill: false,
    },
  ]);
  const navigate = useNavigate();
  const [touRates, setTOURates] = useState<TOURate[]>([
    {
      name: "",
      startTime: "",
      endTime: "",
      rate: "",
      showOnBill: false,
      description: "",
    },
  ]);

  const customerCategoryOptions: SelectOption[] = [
    { value: "Residential", label: "Residential" },
    { value: "Commercial", label: "Commercial" },
    { value: "Industrial", label: "Industrial" },
  ];

  const commodityOptions: SelectOption[] = [
    { value: "Gas", label: "Gas" },
    { value: "Electricity", label: "Electricity" },
    { value: "Water", label: "Water" },
  ];

  const [loading, setLoading] = useState({ draft: false, active: false });

  useEffect(() => {
    if (location.state?.tariffData) {
      const tariffData = location.state.tariffData as TariffResponseT;
      console.log(tariffData, "update taridd data---");
      setTariffData({
        scheduleName: tariffData.scheduleName,
        commodityType: tariffData.scheduleData.commodityType,
        customerCategory: tariffData.scheduleData.customerCategory,
        effectiveDate: new Date(tariffData.scheduleData.effectiveDate),
        timeOfUseRates: tariffData.scheduleData.timeOfUseRates,
      });

      setStatus(tariffData.status);

      tariffData.scheduleData.rateType.forEach((item) => {
        if (item.type == "rateBlocks") {
          setBlocks(item.details);
        } else if (item.type == "flatRates") {
          setFlatRates(item.details);
        } else if (item.type == "tax") {
          setTaxes(item.details);
        }
      });
    }
  }, [location.state]);

  const handleChangeTariffData = <K extends keyof TariffDataT>(
    field: K,
    value: TariffDataT[K],
  ) => {
    setTariffData({ ...tariffData, [field]: value });
  };

  const handleAddRate = () => {
    setFlatRates([...flatRates, { rateUnit: "", rate: "", showOnBill: false }]);
  };

  const handleChangeRate = <K extends keyof FlatRate>(
    index: number,
    field: K,
    value: FlatRate[K],
  ) => {
    setFlatRates(
      flatRates.map((item, i) =>
        i === index ? { ...item, [field]: value } : item,
      ),
    );
  };

  const handleRemoveRate = (index: number) => {
    setFlatRates(flatRates.filter((_, i) => i !== index));
  };

  const handleAddBlock = () => {
    setBlocks([
      ...blocks,
      {
        minConsumption: "",
        maxConsumption: "",
        rate: "",
        tierName: "",
        rateUnit: "per kWh",
        showOnBill: false,
      },
    ]);
  };

  const handleChangeBlock = <K extends keyof Block>(
    index: number,
    field: K,
    value: Block[K],
  ) => {
    setBlocks(
      blocks.map((item, i) =>
        i === index ? { ...item, [field]: value } : item,
      ),
    );
  };

  const handleRemoveBlock = (index: number) => {
    setBlocks(blocks.filter((_, i) => i !== index));
  };

  const handleAddTax = () => {
    setTaxes([...taxes, { rateUnit: "", rate: "", showOnBill: false }]);
  };

  const handleChangeTax = <K extends keyof Tax>(
    index: number,
    field: K,
    value: Tax[K],
  ) => {
    setTaxes(
      taxes.map((item, i) =>
        i === index ? { ...item, [field]: value } : item,
      ),
    );
  };

  const handleRemoveTax = (index: number) => {
    setTaxes(taxes.filter((_, i) => i !== index));
  };

  const handleAddTOU = () => {
    setTOURates([
      ...touRates,
      {
        name: "",
        startTime: "",
        endTime: "",
        rate: "",
        showOnBill: false,
        description: "",
      },
    ]);
  };

  const handleChangeTOU = <K extends keyof TOURate>(
    index: number,
    field: K,
    value: TOURate[K],
  ) => {
    setTOURates(
      touRates.map((item, i) =>
        i === index ? { ...item, [field]: value } : item,
      ),
    );
  };

  const handleRemoveTOU = (index: number) => {
    setTOURates(touRates.filter((_, i) => i !== index));
  };

  const handleSaveDarft = async () => {
    const typeRates: RateType[] = [];

    if (flatRates.length > 0) {
      typeRates.push({
        type: "flatRates",
        details: flatRates,
      });
    }
    if (blocks.length > 0) {
      typeRates.push({
        type: "rateBlocks",
        details: blocks,
      });
    }
    if (taxes.length > 0) {
      typeRates.push({
        type: "tax",
        details: taxes,
      });
    }

    const data: TariffCreateT = {
      scheduleName: tariffData.scheduleName,
      scheduleData: {
        rateType: typeRates,
        commodityType: tariffData.commodityType,
        effectiveDate: formatDate(tariffData.effectiveDate, "YYYY-MM-DD"),
        timeOfUseRates: tariffData.timeOfUseRates,
        customerCategory: tariffData.customerCategory,
        rateScheduleName: tariffData.scheduleName,
      },
      validFrom: formatDate(tariffData.effectiveDate),
    };

    console.log(data, "data---");

    setLoading({ active: false, draft: true });
    if (location.state?.tariffData?.status === "draft") {
      try {
        const response = await axios.put(
          `https://backend.voltreum.com/billing/tariffs/draft/${location.state?.tariffData?.id}`,
          data,
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        );
        console.log(response.data, "response  update ");

        setToast({
          message: "Draft Updated successfully",
          severity: "success",
        });
      } catch (error) {
        console.error("Error Updating Draft:", error);

        setToast({
          message: "Error Updating Draft",
          severity: "error",
        });
      } finally {
        setLoading({ active: false, draft: false });
      }
    } else {
      try {
        const response = await axios.post(
          "https://backend.voltreum.com/billing/tariffs/draft",
          data,
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        );

        console.log(response.data, "response  create ----");

        setToast({
          message: "Draft saved successfully",
          severity: "success",
        });
      } catch (error) {
        const axiosError = error as AxiosError;
        console.error("Error Saving Draft:", axiosError.response?.data);

        setToast({
          message: "Error Saving Draft",
          severity: "error",
        });
      } finally {
        setLoading({ active: false, draft: false });
      }
    }
  };

  const handleSavePublish = async () => {
    if (!location.state?.tariffData?.id) {
      setToast({
        message: "Please save as draft first",
        severity: "error",
      });
      return;
    }

    const data = {
      draftId: location.state?.tariffData?.id,
      scheduleName: tariffData.scheduleName,
      newValidFrom: formatDate(tariffData.effectiveDate),
    };

    console.log(data, "data---");

    setLoading({ active: true, draft: false });

    try {
      const response = await axios.post(
        "https://backend.voltreum.com/billing/tariffs/publish",
        data,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      console.log(response.data, "response  publish ----");

      setToast({
        message: "Tariff published successfully",
        severity: "success",
      });
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("Error Publishing Tariff:", axiosError.response?.data);

      setToast({
        message: "Error Publishing Tariff",
        severity: "error",
      });
    } finally {
      setLoading({ active: false, draft: false });
    }
  };

  const rowsContent = touRates?.map((item: TOURate, index: number) => (
    <TableRow
      key={index}
      sx={{
        height: "50px",
        background: (index + 1) % 2 ? "#F9FCFE" : "transparent",
      }}
      className="custom_table_tbody"
    >
      <TableCell sx={{ padding: "0 10px 10px" }}>
        <Typography className="custom_table_tbody_cell">
          <CustomInput
            onChange={(e) => handleChangeTOU(index, "name", e.target.value)}
            value={item.name}
          />
        </Typography>
      </TableCell>
      <TableCell sx={{ padding: "0 10px 10px" }}>
        <Typography className="custom_table_tbody_cell">
          <CustomInput
            onChange={(e) =>
              handleChangeTOU(index, "description", e.target.value)
            }
            value={item.description}
          />
        </Typography>
      </TableCell>
      <TableCell sx={{ padding: "0 10px 10px" }}>
        <Typography className="custom_table_tbody_cell">
          <CustomInput
            type="time"
            onChange={(e) =>
              handleChangeTOU(index, "startTime", e.target.value)
            }
            value={item.startTime}
          />
        </Typography>
      </TableCell>

      <TableCell sx={{ padding: "0 10px 10px" }}>
        <Typography className="custom_table_tbody_cell">
          <CustomInput
            type="time"
            onChange={(e) => handleChangeTOU(index, "endTime", e.target.value)}
            value={item.endTime}
          />
        </Typography>
      </TableCell>

      <TableCell sx={{ padding: "0 10px 10px" }}>
        <Typography className="custom_table_tbody_cell">
          <CustomInput
            type="number"
            onChange={(e) => handleChangeTOU(index, "rate", e.target.value)}
            value={item.rate}
          />
        </Typography>
      </TableCell>

      <TableCell sx={{ padding: "0 10px 10px" }}>
        <Typography className="custom_table_tbody_cell">
          <Checkbox
            checked={item.showOnBill}
            onChange={(e) =>
              handleChangeTOU(index, "showOnBill", e.target.checked)
            }
          />
        </Typography>
      </TableCell>

      <TableCell sx={{ padding: "0 10px 10px" }}>
        <ContainedButton
          label={"Remove"}
          bgColor={"#FF3B30"}
          hoverBgColor={"#FA6B6B"}
          sx={{
            width: "fit-content",
            fontWeight: 600,
            color: "#ffffff",
          }}
          onClick={() => handleRemoveTOU(index)}
        />
      </TableCell>
    </TableRow>
  ));

  const rowsContentFlatRate = flatRates?.map(
    (item: FlatRate, index: number) => (
      <TableRow
        key={index}
        sx={{
          height: "50px",
          background: (index + 1) % 2 ? "#F9FCFE" : "transparent",
        }}
        className="custom_table_tbody"
      >
        <TableCell sx={{ padding: "0 10px 10px" }}>
          <Typography className="custom_table_tbody_cell">
            <CustomInput
              type="text"
              onChange={(e) =>
                handleChangeRate(index, "rateUnit", e.target.value)
              }
              value={item.rateUnit}
            />
          </Typography>
        </TableCell>

        <TableCell sx={{ padding: "0 10px 10px" }}>
          <Typography className="custom_table_tbody_cell">
            <CustomInput
              type="number"
              onChange={(e) => handleChangeRate(index, "rate", e.target.value)}
              value={item.rate}
            />
          </Typography>
        </TableCell>

        <TableCell sx={{ padding: "0 10px 10px" }}>
          <Checkbox
            checked={item.showOnBill}
            onChange={(e) =>
              handleChangeRate(index, "showOnBill", e.target.checked)
            }
          />
        </TableCell>

        <TableCell sx={{ padding: "0 10px 10px" }}>
          <ContainedButton
            onClick={() => handleRemoveRate(index)}
            label={"Remove"}
            bgColor={"#FF3B30"}
            hoverBgColor={"#FA6B6B"}
            sx={{
              width: "fit-content",
              fontWeight: 600,
              color: "#ffffff",
            }}
          />
        </TableCell>
      </TableRow>
    ),
  );

  const rowsContentTax = taxes?.map((item: Tax, index: number) => (
    <TableRow
      key={index}
      sx={{
        height: "50px",
        background: (index + 1) % 2 ? "#F9FCFE" : "transparent",
      }}
      className="custom_table_tbody"
    >
      <TableCell sx={{ padding: "0 10px 10px" }}>
        <Typography className="custom_table_tbody_cell">
          <CustomInput
            type="text"
            onChange={(e) => handleChangeTax(index, "rateUnit", e.target.value)}
            value={item.rateUnit}
          />
        </Typography>
      </TableCell>

      <TableCell sx={{ padding: "0 10px 10px" }}>
        <Typography className="custom_table_tbody_cell">
          <CustomInput
            type="number"
            onChange={(e) => handleChangeTax(index, "rate", e.target.value)}
            value={item.rate}
          />
        </Typography>
      </TableCell>

      <TableCell sx={{ padding: "0 10px 10px" }}>
        <Checkbox
          checked={item.showOnBill}
          onChange={(e) =>
            handleChangeTax(index, "showOnBill", e.target.checked)
          }
        />
      </TableCell>

      <TableCell sx={{ padding: "0 10px 10px" }}>
        <ContainedButton
          onClick={() => handleRemoveTax(index)}
          label={"Remove"}
          bgColor={"#FF3B30"}
          hoverBgColor={"#FA6B6B"}
          sx={{
            width: "fit-content",
            fontWeight: 600,
            color: "#ffffff",
          }}
        />
      </TableCell>
    </TableRow>
  ));

  const rowsContent1 = blocks?.map((item: Block, index: number) => (
    <TableRow
      key={index}
      sx={{
        height: "50px",
        background: (index + 1) % 2 ? "#F9FCFE" : "transparent",
      }}
      className="custom_table_tbody"
    >
      <TableCell sx={{ padding: "0 10px 10px" }}>
        <Typography className="custom_table_tbody_cell">
          <CustomInput
            type="text"
            onChange={(e) =>
              handleChangeBlock(index, "tierName", e.target.value)
            }
            value={item.tierName}
          />
        </Typography>
      </TableCell>

      <TableCell sx={{ padding: "0 10px 10px" }}>
        <Typography className="custom_table_tbody_cell">
          <CustomInput
            type="number"
            onChange={(e) =>
              handleChangeBlock(index, "minConsumption", e.target.value)
            }
            value={item.minConsumption}
          />
        </Typography>
      </TableCell>

      <TableCell sx={{ padding: "0 10px 10px" }}>
        <Typography className="custom_table_tbody_cell">
          <CustomInput
            type="number"
            onChange={(e) =>
              handleChangeBlock(index, "maxConsumption", e.target.value)
            }
            value={item.maxConsumption}
          />
        </Typography>
      </TableCell>

      <TableCell sx={{ padding: "0 10px 10px" }}>
        <Typography className="custom_table_tbody_cell">
          <CustomInput
            type="number"
            onChange={(e) => handleChangeBlock(index, "rate", e.target.value)}
            value={item.rate}
          />
        </Typography>
      </TableCell>
      <TableCell sx={{ padding: "0 10px 10px" }}>
        <Checkbox
          checked={item.showOnBill}
          onChange={(e) =>
            handleChangeBlock(index, "showOnBill", e.target.checked)
          }
        />
      </TableCell>

      <TableCell sx={{ padding: "0 10px 10px" }}>
        <ContainedButton
          onClick={() => handleRemoveBlock(index)}
          label={"Remove"}
          bgColor={"#FF3B30"}
          hoverBgColor={"#FA6B6B"}
          sx={{
            width: "fit-content",
            fontWeight: 600,
            color: "#ffffff",
          }}
        />
      </TableCell>
    </TableRow>
  ));

  return (
    <Layout>
      <CustomToast
        open={toast.message}
        severity={toast.severity}
        setToast={setToast}
      />
      <Card>
        <CardContent sx={{ padding: "0px !important" }}>
          <Stack
            p={"10px"}
            flexDirection={"row"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <Stack>
              <Typography variant="h5">Tariff Details</Typography>
            </Stack>
            <Stack direction={"row"} gap={"10px"} alignItems={"center"}>
              <OutlinedButton
                label={"Cancel"}
                color={"#1976d2"}
                borderColor={"#1976d2"}
                hoverBorderColor={"#ffffff"}
                hoverBgColor={"#1976d2"}
                hoverColor={"#ffffff"}
                onClick={() => navigate(-1)}
              />
              <ContainedButton
                label={
                  location.state?.tariffData?.status === "draft"
                    ? "Update Draft"
                    : "Save as Draft"
                }
                sx={{
                  width: "fit-content",
                  fontWeight: 600,
                  color: "#ffffff",
                }}
                onClick={handleSaveDarft}
                disabled={status === "inactive"}
                loading={loading.draft}
              />
              <ContainedButton
                label={"Publish"}
                sx={{
                  width: "fit-content",
                  fontWeight: 600,
                  color: "#ffffff",
                }}
                disabled={status === "active" || status === "inactive"}
                loading={loading.active}
                onClick={handleSavePublish}
              />
            </Stack>
          </Stack>

          <Divider />
          <Stack p={"18px"}>
            <Grid container spacing={2.25}>
              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <CustomInput
                  label={"Rate Schedule Name"}
                  value={tariffData.scheduleName}
                  onChange={(e) =>
                    handleChangeTariffData("scheduleName", e.target.value)
                  }
                />
              </Grid>
              {/* <Grid size={{ xs: 12, md: 4, lg: 4 }}>
              <CustomInput label={" Rate Schedule ID"} />
            </Grid> */}
              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <CustomOutlinedSelect
                  label={"Commodity Type"}
                  placeholder={"Select Commodity"}
                  options={commodityOptions}
                  value={tariffData.commodityType}
                  onChange={(e) =>
                    handleChangeTariffData("commodityType", e.value.toString())
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <CustomOutlinedSelect
                  label={"Customer Category"}
                  placeholder={"Select Category"}
                  options={customerCategoryOptions}
                  value={tariffData.customerCategory}
                  onChange={(e) =>
                    handleChangeTariffData(
                      "customerCategory",
                      e.value.toString(),
                    )
                  }
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <DatePickerInput
                  value={tariffData.effectiveDate}
                  label={"Effective Date"}
                  format={"dd/MM/yyyy"}
                  placeholder={"dd/mm/yyyy"}
                  onChange={(e) => handleChangeTariffData("effectiveDate", e)}
                />
              </Grid>
            </Grid>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mt: "20px" }}>
        <CardHeader title={<Typography variant="h5">Rate Type</Typography>} />
        <Divider />

        <CardContent sx={{ p: "0px !important" }}>
          <Accordion sx={{ boxShadow: "none" }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                // p: "0px !important",
                margin: "0px !important",
                minHeight: "36px",

                "&.Mui-expanded": {
                  minHeight: "36px",
                  margin: "0px !important",
                },

                "& .MuiAccordionSummary-content.Mui-expanded": {
                  margin: "12px 0px",
                },
              }}
            >
              <Typography variant="body1">Flat Rates</Typography>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                padding: "0px !important",
              }}
            >
              <Card>
                <CardHeader
                  title={
                    <Stack
                      direction={"row"}
                      justifyContent={"flex-end"}
                      alignItems={"center"}
                    >
                      <Button
                        variant="text"
                        onClick={handleAddRate}
                        sx={{
                          color: "#1976d2",
                          fontWeight: 600,
                          fontSize: "15px",
                          textTransform: "none",
                        }}
                      >
                        Add Flat Rate
                      </Button>
                    </Stack>
                  }
                  sx={{
                    py: "0px !important",
                  }}
                />

                <CardContent
                  sx={{
                    py: "0px !important",
                    borderRadius: 2,
                    overflow: "hidden",
                  }}
                >
                  <CustomDataTable
                    sx={{ maxHeight: "600px" }}
                    columns={FlatRateColumns}
                    pagination={false}
                    rowsContent={rowsContentFlatRate}
                  />
                </CardContent>
              </Card>
            </AccordionDetails>
          </Accordion>

          <Accordion
            sx={{
              boxShadow: "none",
              "&.Mui-expanded": {
                borderTop: "1px solid #ccc",
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                // p: "0px !important",
                margin: "0px !important",
                minHeight: "36px",

                "&.Mui-expanded": {
                  minHeight: "36px",
                  margin: "0px !important",
                },

                "& .MuiAccordionSummary-content.Mui-expanded": {
                  margin: "12px 0px",
                },
              }}
            >
              <Typography variant="body1">Rate Blocks</Typography>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                padding: "0px !important",
              }}
            >
              <Card>
                <CardHeader
                  title={
                    <Stack
                      direction={"row"}
                      justifyContent={"flex-end"}
                      alignItems={"center"}
                    >
                      {/* <ContainedButton
                        label={"Add Block"}
                        
                        sx={{
                          width: "fit-content",
                          fontWeight: 600,
                          color: "#ffffff",
                        }}
                        onClick={handleAddBlock}
                      /> */}
                      <Button
                        variant="text"
                        onClick={handleAddBlock}
                        sx={{
                          color: "#1976d2",
                          fontWeight: 600,
                          fontSize: "15px",
                          textTransform: "none",
                        }}
                      >
                        Add Block
                      </Button>
                    </Stack>
                  }
                  sx={{
                    py: "0px !important",
                  }}
                />

                <CardContent
                  sx={{
                    py: "0px !important",
                    borderRadius: 2,
                    overflow: "hidden",
                  }}
                >
                  <CustomDataTable
                    sx={{ maxHeight: "600px" }}
                    columns={COLUMNS1}
                    pagination={false}
                    rowsContent={rowsContent1}
                  />
                </CardContent>
              </Card>
            </AccordionDetails>
          </Accordion>

          <Accordion
            sx={{
              boxShadow: "none",
              "&.Mui-expanded": {
                borderTop: "1px solid #ccc",
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                // p: "0px !important",
                margin: "0px !important",
                minHeight: "36px",

                "&.Mui-expanded": {
                  minHeight: "36px",
                  margin: "0px !important",
                },

                "& .MuiAccordionSummary-content.Mui-expanded": {
                  margin: "12px 0px",
                },
              }}
            >
              <Typography variant="body1">Demand-based</Typography>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                padding: "0px !important",
              }}
            ></AccordionDetails>
          </Accordion>

          <Accordion
            sx={{
              boxShadow: "none",
              "&.Mui-expanded": {
                borderTop: "1px solid #ccc",
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                // p: "0px !important",
                margin: "0px !important",
                minHeight: "36px",

                "&.Mui-expanded": {
                  minHeight: "36px",
                  margin: "0px !important",
                },

                "& .MuiAccordionSummary-content.Mui-expanded": {
                  margin: "12px 0px",
                },
              }}
            >
              <Typography variant="body1">Taxes</Typography>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                padding: "0px !important",
              }}
            >
              <Card>
                <CardHeader
                  title={
                    <Stack
                      direction={"row"}
                      justifyContent={"flex-end"}
                      alignItems={"center"}
                    >
                      {/* <ContainedButton
                        label={"Add Tax"}
                        
                        sx={{
                          width: "fit-content",
                          fontWeight: 600,
                          color: "#ffffff",
                        }}
                        onClick={handleAddTax}
                      /> */}
                      <Button
                        variant="text"
                        onClick={handleAddTax}
                        sx={{
                          color: "#1976d2",
                          fontWeight: 600,
                          fontSize: "15px",
                          textTransform: "none",
                        }}
                      >
                        Add Tax
                      </Button>
                    </Stack>
                  }
                  sx={{
                    py: "0px !important",
                  }}
                />

                <CardContent
                  sx={{
                    pt: "0px !important",
                    borderRadius: 2,
                    overflow: "hidden",
                  }}
                >
                  <CustomDataTable
                    sx={{ maxHeight: "600px" }}
                    columns={TaxColumns}
                    pagination={false}
                    rowsContent={rowsContentTax}
                  />
                </CardContent>
              </Card>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>

      <Card sx={{ mt: "20px", maxHeight: "650px" }}>
        <CardHeader
          title={
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Typography variant="h5">Time of Use Rates</Typography>
              <CustomSwitch
                value={tariffData.timeOfUseRates}
                onChange={() =>
                  handleChangeTariffData(
                    "timeOfUseRates",
                    !tariffData.timeOfUseRates,
                  )
                }
              />
            </Stack>
          }
        />
        <Divider />

        <CardContent>
          {tariffData.timeOfUseRates && (
            <>
              <CustomDataTable
                sx={{ maxHeight: "600px" }}
                columns={COLUMNS}
                pagination={false}
                rowsContent={rowsContent}
              />

              <Stack direction={"row"} justifyContent={"center"} mt={"15px"}>
                <ContainedButton
                  label={"Add Period"}
                  sx={{
                    width: "fit-content",
                    fontWeight: 600,
                    color: "#ffffff",
                  }}
                  onClick={handleAddTOU}
                />
              </Stack>
            </>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mt: "20px" }}>
        <CardHeader
          title={<Typography variant="h5">Rate Summary</Typography>}
        />
        <Divider />

        <CardContent>
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <Typography variant="body1">Rate Type</Typography>
            <Typography variant="caption">
              {flatRates.length > 0 ? "Flat Rate" : "Block Rate"}
            </Typography>
          </Stack>

          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            alignItems={"center"}
            mt={"10px"}
          >
            <Typography variant="body1">Features</Typography>
            <Stack>
              {tariffData.timeOfUseRates && (
                <Typography variant="caption">TOU Rates</Typography>
              )}
              {hasSeasonalRates && (
                <Typography variant="caption">Seasonal</Typography>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Layout>
  );
};

export default RateScheduleForm;
